import { createFileRoute, Link } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, jobById, candidatesByJob, type Job, type Candidate } from '@/lib/mocks'
import { PageHeader, Avatar, MatchScore } from '@/lib/atsUi'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { DataList } from '@/components/ui/data-list'
import { EmptyState } from '@/components/states'
import { MapPin, IndianRupee, Briefcase, Users, Sparkles, ArrowLeft } from 'lucide-react'

export const Route = createFileRoute('/jobs/$jobId')({
  component: JobDetailPage,
})

function statusVariant(status: Job['status']): 'success' | 'warning' | 'info' | 'error' {
  switch (status) {
    case 'open': return 'success'
    case 'on_hold': return 'warning'
    case 'filled': return 'info'
    case 'draft': return 'info'
  }
}

function statusLabel(status: Job['status']): string {
  switch (status) {
    case 'open': return 'Open'
    case 'on_hold': return 'On hold'
    case 'filled': return 'Filled'
    case 'draft': return 'Draft'
  }
}

function stageLabel(stage: Candidate['stage']): string {
  switch (stage) {
    case 'sourced': return 'Sourced'
    case 'screened': return 'Screened'
    case 'l1': return 'L1'
    case 'l2': return 'L2'
    case 'offer': return 'Offer'
    case 'joined': return 'Joined'
  }
}

function stageVariant(stage: Candidate['stage']): 'success' | 'warning' | 'info' | 'error' {
  switch (stage) {
    case 'joined': return 'success'
    case 'offer': return 'success'
    case 'l2': return 'info'
    case 'l1': return 'info'
    case 'screened': return 'warning'
    case 'sourced': return 'warning'
  }
}

interface JdSection {
  about: string[]
  responsibilities: string[]
  requirements: string[]
}

function generateJd(job: Job): JdSection {
  const title = job.title.toLowerCase()

  if (title.includes('product manager') || title.includes('product')) {
    return {
      about: [
        `${job.client} is looking for an experienced Product Manager to own the roadmap and drive product strategy across key business verticals.`,
        `You will work closely with engineering, design, and business stakeholders to define, prioritise, and ship features that create measurable customer impact.`,
      ],
      responsibilities: [
        'Define and communicate a clear product vision and roadmap aligned with company OKRs',
        'Conduct user research, analyse data, and synthesise insights to inform prioritisation decisions',
        'Write crisp PRDs and user stories; drive sprint planning alongside engineering leads',
        'Own end-to-end feature delivery from discovery through launch and iteration',
      ],
      requirements: [
        '6+ years of product management experience in a B2B SaaS or fintech environment',
        'Demonstrated track record of shipping 0-to-1 products and iterating post-launch',
        'Strong analytical skills with hands-on experience in SQL, Mixpanel, or equivalent tools',
        'Excellent written and verbal communication; able to influence without authority',
      ],
    }
  }

  if (title.includes('data') && title.includes('engineer')) {
    return {
      about: [
        `${job.client} is hiring a Senior Data Engineer to build and maintain scalable data pipelines that power analytics and ML initiatives across the organisation.`,
        `You will be the cornerstone of the data platform team, ensuring data reliability, quality, and accessibility for both technical and business consumers.`,
      ],
      responsibilities: [
        'Design and implement robust ELT/ETL pipelines using Spark, dbt, and Airflow',
        'Architect and maintain a modern data lakehouse on cloud infrastructure (AWS/GCP)',
        'Collaborate with data scientists to productionise ML feature pipelines',
        'Define data contracts, schema governance, and observability standards',
      ],
      requirements: [
        '5+ years of data engineering experience with strong Python and SQL skills',
        'Hands-on expertise with Apache Spark, dbt, and workflow orchestration tools like Airflow or Prefect',
        'Experience with cloud data warehouses such as BigQuery, Snowflake, or Redshift',
        'Solid understanding of data modelling, Kimball methodology, and streaming architectures',
      ],
    }
  }

  if (title.includes('frontend') || title.includes('react') || title.includes('ui')) {
    return {
      about: [
        `${job.client} is looking for a Senior Frontend Engineer to lead the development of high-performance, accessible user interfaces used by thousands of customers daily.`,
        `You will set the bar for frontend quality, define component architecture, and mentor a growing team of engineers building world-class product experiences.`,
      ],
      responsibilities: [
        'Architect and build scalable React component libraries and design system integrations',
        'Drive performance optimisations — Core Web Vitals, bundle size, rendering efficiency',
        'Collaborate with product designers to translate complex UX requirements into clean implementations',
        'Establish testing standards using Vitest and Playwright across the frontend stack',
      ],
      requirements: [
        '6+ years of frontend engineering with deep React and TypeScript expertise',
        'Strong command of web performance, accessibility (WCAG 2.1), and browser internals',
        'Experience building and maintaining design systems at scale',
        'Familiarity with state management patterns (Zustand, Redux, TanStack Query)',
      ],
    }
  }

  if (title.includes('backend') || title.includes('node') || title.includes('java') || title.includes('python')) {
    return {
      about: [
        `${job.client} is seeking a Senior Backend Engineer to design and deliver reliable, high-throughput services that form the backbone of its core platform.`,
        `You will work in a cross-functional squad, owning entire service domains from architecture through to production operations.`,
      ],
      responsibilities: [
        'Design and implement microservices with a focus on reliability, latency, and scalability',
        'Define API contracts and integrate with third-party systems and payment gateways',
        'Participate in on-call rotations; own incident response and post-mortems for your domain',
        'Champion engineering best practices — code reviews, documentation, and test coverage',
      ],
      requirements: [
        '5+ years of backend engineering; proficient in Node.js, Java, or Python',
        'Experience building and operating distributed systems in production at scale',
        'Strong understanding of relational databases, query optimisation, and indexing strategies',
        'Familiarity with message queues (Kafka, RabbitMQ) and event-driven architecture',
      ],
    }
  }

  if (title.includes('engineering manager') || title.includes('manager')) {
    return {
      about: [
        `${job.client} is hiring an Engineering Manager to lead and grow a high-performing squad delivering critical product capabilities.`,
        `You will be the intersection of people leadership, technical strategy, and cross-functional execution — setting the team up for speed and quality.`,
      ],
      responsibilities: [
        'Lead, hire, and develop a team of 6–10 engineers across multiple experience levels',
        'Partner with product to set quarterly goals, break down ambiguity, and align on delivery',
        'Drive technical direction, architecture reviews, and engineering excellence initiatives',
        'Run effective 1:1s, performance cycles, and career growth conversations',
      ],
      requirements: [
        '8+ years of engineering experience with 3+ years in an engineering management role',
        'Proven track record of building and retaining high-performing engineering teams',
        'Deep technical fluency — comfortable diving into system design, code reviews, and incident response',
        'Strong communication skills; ability to translate between technical and business stakeholders',
      ],
    }
  }

  if (title.includes('devops') || title.includes('platform') || title.includes('infra')) {
    return {
      about: [
        `${job.client} is seeking a DevOps Lead to own and evolve the cloud infrastructure, CI/CD platform, and developer experience across the engineering organisation.`,
        `You will be the force multiplier that helps every product squad ship faster and more reliably.`,
      ],
      responsibilities: [
        'Own the cloud infrastructure (AWS/GCP) including networking, security, and cost management',
        'Build and maintain CI/CD pipelines with a focus on speed, reliability, and developer ergonomics',
        'Define and enforce SLOs, drive incident management, and lead post-mortems',
        'Champion IaC practices using Terraform; ensure environments are reproducible and auditable',
      ],
      requirements: [
        '7+ years of DevOps or platform engineering experience in a cloud-native environment',
        'Deep expertise with Kubernetes, Terraform, and at least one major cloud provider',
        'Experience designing and operating high-availability production systems at scale',
        'Strong scripting skills (Python or Go); familiarity with observability stacks (Datadog, Prometheus)',
      ],
    }
  }

  if (title.includes('data scientist') || title.includes('ml') || title.includes('machine learning')) {
    return {
      about: [
        `${job.client} is looking for a Data Scientist to build models and insights that directly influence product decisions and business outcomes.`,
        `You will collaborate with engineering and product teams to translate business problems into rigorous data science solutions.`,
      ],
      responsibilities: [
        'Build, validate, and deploy ML models across recommendation, ranking, and risk domains',
        'Design and run A/B experiments; translate results into clear business recommendations',
        'Collaborate with data engineers to ensure feature pipelines are production-ready',
        'Present findings to non-technical stakeholders with clarity and confidence',
      ],
      requirements: [
        '4+ years of data science experience with a strong statistics and ML foundations',
        'Proficient in Python and its ML ecosystem (scikit-learn, PyTorch, or TensorFlow)',
        'Hands-on experience with SQL, Spark, and cloud-based data platforms',
        'Excellent communication skills; able to translate model outputs into business narratives',
      ],
    }
  }

  return {
    about: [
      `${job.client} is hiring a ${job.title} to drive meaningful outcomes across a fast-growing business operating in a competitive market.`,
      `This is a high-impact role where you will work alongside talented peers and senior leadership to shape strategy and execution at scale.`,
    ],
    responsibilities: [
      `Lead end-to-end ownership of key ${job.title} deliverables with measurable KPIs`,
      'Collaborate cross-functionally with product, engineering, and business stakeholders',
      'Identify process gaps and implement structured improvements for team efficiency',
      'Report progress to leadership and proactively surface risks and mitigation plans',
    ],
    requirements: [
      `6+ years of progressive experience in a ${job.title} or closely related function`,
      'Proven ability to operate autonomously and deliver under ambiguity',
      'Strong communication skills with the ability to influence senior decision-makers',
      'Prior experience at a growth-stage startup or mid-market company is preferred',
    ],
  }
}

function FactChip({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <span className="shrink-0">{icon}</span>
      <span>{value}</span>
    </div>
  )
}

function JobDetailPage() {
  const { jobId } = Route.useParams()
  const job = jobById(jobId)

  if (!job) {
    return (
      <DashboardLayout title="Job not found" navSections={atsNav} tenant={currentTenant} user={currentUser}>
        <EmptyState title="Job not found" />
      </DashboardLayout>
    )
  }

  const jd = generateJd(job)

  return (
    <DashboardLayout title={job.title} navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <div className="mb-4">
        <Link
          to="/jobs"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Jobs
        </Link>
      </div>

      <PageHeader
        title={job.title}
        subtitle="The role, its AI-drafted description, and candidates."
      />

      <div className="space-y-6">
        {/* Role header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">{job.client}</p>
          <Badge variant={statusVariant(job.status)} className="w-fit">
            {statusLabel(job.status)}
          </Badge>
        </div>

        {/* Facts row */}
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <FactChip icon={<MapPin className="h-3.5 w-3.5" />} value={job.location} />
          <FactChip icon={<IndianRupee className="h-3.5 w-3.5" />} value={job.ctcRange} />
          <FactChip icon={<Briefcase className="h-3.5 w-3.5" />} value={`${job.feePct}% fee`} />
          <FactChip icon={<Users className="h-3.5 w-3.5" />} value={`${job.openings} opening${job.openings !== 1 ? 's' : ''}`} />
        </div>

        {/* Two-column: JD (left) + candidates (right) */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr] lg:items-start">
          {/* AI-generated JD */}
          <Card>
          <CardContent className="pt-5">
            <div className="mb-4 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Job description</span>
              <Badge variant="info">AI-generated</Badge>
            </div>

            <div className="space-y-4 text-sm">
              <section>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">About the role</h2>
                {jd.about.map((line, i) => (
                  <p key={i} className={`text-foreground${i > 0 ? ' mt-1.5' : ''}`}>{line}</p>
                ))}
              </section>

              <section>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">What you'll do</h2>
                <ul className="list-disc space-y-1 pl-4 text-foreground">
                  {jd.responsibilities.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">What we're looking for</h2>
                <ul className="list-disc space-y-1 pl-4 text-foreground">
                  {jd.requirements.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>
          </CardContent>
        </Card>

        {/* Candidates section */}
        <section>
          <h2 className="text-sm font-medium text-muted-foreground mb-3">
            Candidates
          </h2>

          <DataList
            items={candidatesByJob(jobId)}
            getKey={(c) => c.id}
            pageSize={5}
            empty="No candidates yet."
            renderRow={(candidate) => (
              <Link
                to="/candidates/$candidateId"
                params={{ candidateId: candidate.id }}
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40"
              >
                <Avatar name={candidate.name} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{candidate.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {candidate.title} · {candidate.currentCompany}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <MatchScore score={candidate.matchScore} />
                  <Badge variant={stageVariant(candidate.stage)}>
                    {stageLabel(candidate.stage)}
                  </Badge>
                </div>
              </Link>
            )}
          />
        </section>
        </div>
      </div>
    </DashboardLayout>
  )
}
